/**
 * Shared Tab-containment for modal surfaces (WAI-ARIA Dialog Pattern).
 *
 * Four components had hand-rolled, near-identical copies of this logic
 * (FeedbackModal, the extension + web PairConfirmCard rename dialogs, and
 * ClipboardScreen's now-deleted SendConfirmPopover), and CommandPalette declared
 * `aria-modal="true"` with no containment at all — so Tab walked straight
 * out of the "modal" and into the page behind it.
 *
 * Deliberately a plain function rather than a `useFocusTrap` hook. Every
 * call site already owns a keydown handler with its own Escape semantics —
 * FeedbackModal swallows Escape mid-submit, the popover cancels, the
 * palette closes — so a hook that owned the listener would have to
 * re-expose all of that through options and would end up more configuration
 * than code. A pure function drops into the `Tab` branch of a handler that
 * already exists, and is trivially testable without rendering anything.
 */

/**
 * Elements that can receive focus via Tab. `[tabindex="-1"]` is excluded
 * throughout: those are programmatic focus targets (the dialog container
 * itself, listbox rows driven by aria-activedescendant) and must not become
 * Tab stops.
 */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])';

/** Minimal shape of the keyboard event, so React and DOM events both fit. */
interface TabKeyEvent {
  key: string;
  shiftKey: boolean;
  preventDefault(): void;
}

/**
 * Keep Tab focus inside `container`.
 *
 * Call from a keydown handler; it no-ops unless the key is Tab. Returns
 * `true` when it took over the event (so a caller can stop there), `false`
 * when it left the event alone.
 *
 * Three cases, in order:
 *   1. Focus escaped the container — pull it back to the first element.
 *      Without this the wrap-around checks below silently do nothing,
 *      because neither `first` nor `last` is the active element.
 *   2. Shift+Tab on the first element — wrap to the last.
 *   3. Tab on the last element — wrap to the first.
 *
 * With exactly one focusable child, `first === last`, so both wrap cases
 * collapse to "hold focus where it is" — which is the correct behaviour for
 * a dialog whose only control is a text input, and precisely the case
 * CommandPalette was getting wrong.
 */
export function trapTabKey(container: HTMLElement | null, event: TabKeyEvent): boolean {
  if (event.key !== "Tab" || !container) return false;

  const focusable = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
  if (focusable.length === 0) return false;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = container.ownerDocument.activeElement;

  if (!container.contains(active)) {
    event.preventDefault();
    first.focus();
    return true;
  }

  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
    return true;
  }

  if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
    return true;
  }

  return false;
}
